import React, { useEffect, useState } from "react";

import ElementTypeTag from "@v2/features/modifiers/ElementTypeTag";
import { useIframeMessageRouter } from "@v2/features/modifiers/hooks/useIframeMessageRouter";
import { useIframeMessaging } from "@v2/features/modifiers/hooks/useIframeMessaging";
import InlineToolbar from "@v2/features/modifiers/InlineToolbar";
import {
  useDefaultSelectedElementDesignation,
  useDisplayMode,
  useIsIframeLoaded,
  useModifiersStoreActions,
} from "@v2/features/modifiers/store/ModifiersStoreContext";
import { usePage } from "@v2/features/page-renderer/hooks/usePage";
import {
  useEditorDraftStyleLibrary,
  useViewingVersionId,
} from "@v2/store/editorStore";

import PageRenderer from "./PageRenderer";

type ModifiablePageRendererProps = {
  pageRoute: string;
  className?: string;
  isInteractive?: boolean;
  showControls?: boolean;
  previewThumbnail?: boolean;
};

/**
 * A wrapper around PageRenderer that adds modifier functionality.
 * This component should only be used when modifier interactions are needed.
 */
function ModifiablePageRenderer({
  pageRoute,
  className,
  showControls = true,
  isInteractive = false,
  previewThumbnail,
}: ModifiablePageRendererProps) {
  const displayMode = useDisplayMode();
  const isIframeLoaded = useIsIframeLoaded();
  const modifierActions = useModifiersStoreActions();
  const draftStyleLibrary = useEditorDraftStyleLibrary();
  const viewingVersionId = useViewingVersionId();

  // Note (Nick, 2025-12-02): Get iframeKey to detect when iframe remounts (sandbox refresh)
  const { iframeKey: currentIframeKey } = usePage({
    pageRoute,
    overrideStyleLibrary: draftStyleLibrary,
    viewingVersionId,
    forceSkipSandboxSync: !isInteractive,
  });

  const defaultDesignation = useDefaultSelectedElementDesignation();

  const hoverRef = React.useRef<HTMLDivElement>(null);
  const clickedSectionRef = React.useRef<HTMLDivElement>(null);

  const iframeContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      modifierActions.setIframeContainer(node);
    },
    [modifierActions],
  );

  const { sendMessage } = useIframeMessaging();

  const [editBarStyles, setEditBarStyles] = useState<{
    top: number;
    left: number;
    display: string;
  }>({
    top: 0,
    left: 0,
    display: "none",
  });

  const [elementTagStyles, setElementTagStyles] = useState<{
    top: number;
    left: number;
    display: string;
  }>({
    top: 0,
    left: 0,
    display: "none",
  });

  const latestRectRef = React.useRef<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const groupedElementRectsRef = React.useRef<
    { top: number; left: number; width: number; height: number }[]
  >([]);

  const [groupedElementRects, setGroupedElementRects] = useState<
    { top: number; left: number; width: number; height: number }[]
  >([]);

  // Note (Nick, 2025-12-02): Track previous iframeKey to detect when iframe remounts
  const previousIframeKey = React.useRef<string | undefined>(undefined);

  /**
   * NOTE (Kurt, 2025-08-04): Clears selection in iframe when no element is selected.
   *
   * When the user deselects an element, this effect ensures that the iframe
   * is notified to clear its selection state to prevent stale element highlighting.
   */
  useEffect(() => {
    if (!defaultDesignation && showControls) {
      sendMessage({ type: "CLEAR_SELECTION" });
    }
  }, [defaultDesignation, showControls, sendMessage]);

  /**
   * NOTE (Nick, 2025-12-01): Resets modifier state when navigating between pages.
   *
   * When the user navigates to a different page, we reset to interactive mode
   * and clear any selected elements. This ensures clean state on each page and
   * prevents stale element references from the previous page.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: effect intentionally runs on pageRoute change
  useEffect(() => {
    modifierActions.resetToDefaults();
  }, [pageRoute, modifierActions]);

  /**
   * NOTE (Kurt, 2025-08-04): Synchronizes display mode between editor and preview iframe.
   *
   * When the editor switches between interactive and select modes, this effect
   * notifies the iframe so it can adjust its behavior accordingly. In select mode,
   * the iframe intercepts clicks for element selection; in interactive mode, it
   * allows normal user interactions.
   *
   * NOTE (Nick, 2025-12-01): Added pageRoute dependency to ensure the iframe
   * receives the current display mode after page navigation, keeping editor and
   * iframe synchronized.
   *
   * NOTE (Nick, 2025-12-04): Added isIframeLoaded dependency so this effect re-runs
   * when iframe becomes loaded, ensuring the current displayMode is sent after handshake.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: effect intentionally runs on pageRoute change
  useEffect(() => {
    sendMessage({
      type: "SET_DISPLAY_MODE",
      payload: { displayMode },
    });
  }, [displayMode, pageRoute, isIframeLoaded, sendMessage]);

  //  NOTE (Nick, 2025-12-02): Resets editor and iframe state + removes bounding boxes when iframe refreshes
  useEffect(() => {
    if (previousIframeKey.current === undefined) {
      previousIframeKey.current = currentIframeKey;
      return;
    }

    if (previousIframeKey.current !== currentIframeKey) {
      modifierActions.resetToDefaults();

      latestRectRef.current = null;
      if (hoverRef.current) {
        hoverRef.current.style.display = "none";
      }
      if (clickedSectionRef.current) {
        clickedSectionRef.current.style.display = "none";
      }

      sendMessage({ type: "CLEAR_SELECTION" });
      sendMessage({
        type: "SET_DISPLAY_MODE",
        payload: { displayMode },
      });
      previousIframeKey.current = currentIframeKey;
    }
  }, [currentIframeKey, displayMode, modifierActions, sendMessage]);

  /**
   * Centralized router for all iframe-to-parent window messages.
   * Handles FIBER_SELECTED, HOVER_ELEMENT, UPDATE_TEXT, FAST_REFRESH_COMPLETE, etc.
   */
  useIframeMessageRouter({
    showControls,
    hoverRef,
    latestRectRef,
    groupedElementRectsRef,
  });

  /**
   * NOTE (Kurt, 2025-08-04): Continuously updates overlay positioning using requestAnimationFrame.
   *
   * This effect runs every frame to ensure the red border (selected element) and text edit
   * toolbar stay positioned correctly over the selected element. It reads from latestRectRef
   * (updated by message events) and applies smooth visual positioning to the overlay elements.
   *
   * Uses requestAnimationFrame for smooth 60fps updates, essential for responsive visual feedback.
   */
  useEffect(() => {
    if (!showControls) {
      return;
    }

    let animationFrameId: number;

    const updateOverlay = () => {
      const rect = latestRectRef.current;

      if (rect) {
        const { top, left, width, height } = rect;

        // Update red border if it exists
        if (clickedSectionRef.current) {
          Object.assign(clickedSectionRef.current.style, {
            top: `${top}px`,
            left: `${left}px`,
            width: `${width}px`,
            height: `${height}px`,
            display: "block",
          });
        }

        // Update text edit bar styles
        if (
          defaultDesignation?.element.innerHtml &&
          defaultDesignation.element.innerHtml.trim() !== ""
        ) {
          setEditBarStyles({
            top: top + height + 8,
            left: left,
            display: "block",
          });
        } else {
          setEditBarStyles((prevStyles) => ({
            ...prevStyles,
            display: "none",
          }));
        }

        if (defaultDesignation) {
          setElementTagStyles({
            top: top - 30,
            left,
            display: "block",
          });
        } else {
          setElementTagStyles((prevStyles) => ({
            ...prevStyles,
            display: "none",
          }));
        }
      }

      // NOTE (Nick, 2025-12-02): Update grouped element rects from ref to state
      const currentRects = groupedElementRectsRef.current;
      setGroupedElementRects(currentRects);

      animationFrameId = requestAnimationFrame(updateOverlay);
    };

    animationFrameId = requestAnimationFrame(updateOverlay);

    return () => cancelAnimationFrame(animationFrameId);
    /**
     * TODO (Kurt, 2025-08-05): Consider using useBoundingBox() hook instead of
     * manually managing position state with requestAnimationFrame. The useBoundingBox
     * hook provides automatic position tracking with mutation observers and optimized
     * updates. Not implementing now to avoid disrupting current positioning logic.
     */
  }, [showControls, defaultDesignation?.element.innerHtml, defaultDesignation]);

  // Create overlay elements when showControls is true
  const overlayElements = showControls ? (
    <>
      <div
        ref={hoverRef}
        className="absolute border-2 border-dashed border-primary pointer-events-none"
        style={{
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          display: "none",
        }}
      />
      {defaultDesignation && (
        <div
          ref={clickedSectionRef}
          className="absolute border-2 border-primary pointer-events-none"
          style={{
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            display: "none",
          }}
        />
      )}
      {defaultDesignation &&
        groupedElementRects.map((rect, index) => (
          <React.Fragment key={`grouped-${index}`}>
            <div
              className="absolute border-2 border-dashed border-primary pointer-events-none opacity-50"
              style={{
                top: `${rect.top}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                display: "block",
              }}
            />
            <div
              className="absolute pointer-events-none z-20"
              style={{
                top: `${rect.top - 30}px`,
                left: `${rect.left}px`,
                display: "block",
              }}
            >
              <ElementTypeTag elementType={defaultDesignation.element.type} />
            </div>
          </React.Fragment>
        ))}
      {defaultDesignation?.element.innerHtml &&
      defaultDesignation.element.innerHtml.trim() !== "" ? (
        <div
          className="absolute pointer-events-auto z-10"
          style={{
            top: `${editBarStyles.top}px`,
            left: `${editBarStyles.left}px`,
            display: editBarStyles.display,
          }}
        >
          <InlineToolbar />
        </div>
      ) : null}
      {defaultDesignation && (
        <div
          className="absolute pointer-events-none z-20"
          style={{
            top: `${elementTagStyles.top}px`,
            left: `${elementTagStyles.left}px`,
            display: elementTagStyles.display,
          }}
        >
          <ElementTypeTag elementType={defaultDesignation.element.type} />
        </div>
      )}
    </>
  ) : undefined;

  return (
    <PageRenderer
      pageRoute={pageRoute}
      overrideStyleLibrary={draftStyleLibrary}
      viewingVersionId={viewingVersionId}
      className={className}
      isInteractive={isInteractive}
      previewThumbnail={previewThumbnail}
      containerRef={iframeContainerRef}
      overlayElements={overlayElements}
    />
  );
}

export default ModifiablePageRenderer;
